import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, Wrench } from "lucide-react";

const EMPTY = {
  code: "PM-1", name: "", description: "", equipment_id: "",
  interval_hours: 250, filters: [], oils: [], tasks: [], active: true,
};

export default function MntPMTemplates() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const canEdit = can("admin", "maintenance", "warehouse");

  const load = useCallback(() => {
    api.get("/mnt/pm-templates").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {});
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY, filters: [], oils: [], tasks: [] });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      code: t.code || "PM-1",
      name: t.name || "",
      description: t.description || "",
      equipment_id: t.equipment_id || "",
      interval_hours: Number(t.interval_hours || 0),
      filters: (t.filters || []).map((f) => ({ ...f })),
      oils: (t.oils || []).map((o) => ({ ...o })),
      tasks: (t.tasks || []).map((task) => ({ ...task })),
      active: t.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Código y nombre requeridos"); return; }
    try {
      if (editId) await api.put(`/mnt/pm-templates/${editId}`, form);
      else await api.post("/mnt/pm-templates", form);
      toast.success("Plantilla guardada");
      setOpen(false);
      setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Desactivar plantilla ${t.code} · ${t.name}?`)) return;
    try {
      await api.delete(`/mnt/pm-templates/${t.id}`);
      toast.success("Desactivada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addMat = (kind) => setForm({ ...form, [kind]: [...form[kind], { item_number: "", description: "", qty: 1, uom: "EA" }] });
  const removeMat = (kind, idx) => setForm({ ...form, [kind]: form[kind].filter((_, i) => i !== idx) });
  const updateMat = (kind, idx, patch) => setForm({
    ...form, [kind]: form[kind].map((m, i) => (i === idx ? { ...m, ...patch } : m)),
  });
  const pickItemForMat = (kind, idx, itemNumber) => {
    const it = items.find((x) => x.item_number === itemNumber);
    updateMat(kind, idx, {
      item_number: itemNumber,
      description: it?.description || "",
      uom: it?.uom_primary || "EA",
    });
  };

  const addTask = () => setForm({ ...form, tasks: [...form.tasks, { order: form.tasks.length + 1, description: "", category: "" }] });
  const removeTask = (idx) => setForm({ ...form, tasks: form.tasks.filter((_, i) => i !== idx) });
  const updateTask = (idx, patch) => setForm({
    ...form, tasks: form.tasks.map((tk, i) => (i === idx ? { ...tk, ...patch } : tk)),
  });

  const eqLabel = (id) => {
    if (!id) return "Genérico (todos los equipos)";
    const eq = equipment.find((e) => e.id === id);
    return eq ? `${eq.code} · ${eq.name}` : "—";
  };

  return (
    <div data-testid="pm-templates-page">
      <PageHeader title="Plantillas de Mantenimiento Preventivo (PM)"
                  subtitle="Filtros, aceites y gamas de tareas por servicio (PM-1, PM-2…). Alimentan Avisos preventivos.">
        {canEdit && (
          <Button size="sm" className="rounded-none text-xs font-bold" onClick={openNew} data-testid="pm-new-btn">
            <Plus size={12} className="mr-1" /> Nueva plantilla
          </Button>
        )}
      </PageHeader>

      {rows.length === 0 ? <Empty text="Sin plantillas — crea la primera para comenzar" /> : (
        <div className="tbl-wrap" data-testid="pm-table">
          <table className="tbl">
            <thead><tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Equipo</th>
              <th className="num">Intervalo (h)</th>
              <th className="num">Filtros</th>
              <th className="num">Aceites</th>
              <th className="num">Tareas</th>
              <th>Estatus</th>
              <th></th>
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} data-testid={`pm-row-${t.id}`} className={t.active === false ? "opacity-60" : ""}>
                  <td className="font-bold font-mono">{t.code}</td>
                  <td>{t.name}</td>
                  <td className="text-xs">{eqLabel(t.equipment_id)}</td>
                  <td className="num tabular-nums font-bold">{Number(t.interval_hours || 0).toLocaleString()}</td>
                  <td className="num">{(t.filters || []).length}</td>
                  <td className="num">{(t.oils || []).length}</td>
                  <td className="num">{(t.tasks || []).length}</td>
                  <td>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${t.active !== false ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {t.active !== false ? "Activa" : "Inactiva"}
                    </span>
                  </td>
                  <td>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                                onClick={() => openEdit(t)} data-testid={`pm-edit-${t.id}`}>
                          <Pencil size={12} />
                        </Button>
                        {t.active !== false && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-destructive"
                                  onClick={() => remove(t)} data-testid={`pm-delete-${t.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(EMPTY); } }}>
        <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="pm-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench size={16} /> {editId ? "Editar plantilla PM" : "Nueva plantilla PM"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] font-bold uppercase">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     className="rounded-none h-9 text-xs font-mono" placeholder="PM-1"
                     data-testid="pm-form-code" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-bold uppercase">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="rounded-none h-9 text-xs" placeholder="Servicio 250 h"
                     data-testid="pm-form-name" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase">Intervalo (h)</Label>
              <Input type="number" min="0" step="1" value={form.interval_hours}
                     onChange={(e) => setForm({ ...form, interval_hours: Number(e.target.value) })}
                     className="rounded-none h-9 text-xs tabular-nums" data-testid="pm-form-interval" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-bold uppercase">Equipo (vacío = genérico)</Label>
              <Select value={form.equipment_id || "_none"}
                      onValueChange={(v) => setForm({ ...form, equipment_id: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-9 text-xs" data-testid="pm-form-equipment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Genérico (todos los equipos)</SelectItem>
                  {equipment.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-end">
              <label className="flex items-center gap-2 text-xs font-bold">
                <input type="checkbox" checked={form.active}
                       onChange={(e) => setForm({ ...form, active: e.target.checked })}
                       data-testid="pm-form-active" />
                Activa
              </label>
            </div>
            <div className="col-span-full">
              <Label className="text-[10px] font-bold uppercase">Descripción</Label>
              <Textarea value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={2} className="rounded-none text-xs"
                        data-testid="pm-form-description" />
            </div>
          </div>

          {["filters", "oils"].map((kind) => (
            <div key={kind} className="border-t mt-3 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {kind === "filters" ? "Filtros a reemplazar" : "Aceites y lubricantes"} ({form[kind].length})
                </div>
                <Button size="sm" variant="outline" className="rounded-none text-[10px] font-bold h-7"
                        onClick={() => addMat(kind)} data-testid={`pm-add-${kind}`}>
                  <Plus size={11} className="mr-1" /> Agregar
                </Button>
              </div>
              {form[kind].length > 0 && (
                <table className="w-full text-xs border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-1 text-left w-56">Código</th>
                      <th className="p-1 text-left">Descripción</th>
                      <th className="p-1 text-right w-20">Cant.</th>
                      <th className="p-1 text-left w-16">UOM</th>
                      <th className="p-1 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form[kind].map((m, idx) => (
                      <tr key={idx} className="border-t" data-testid={`pm-${kind}-row-${idx}`}>
                        <td className="p-1">
                          <Select value={m.item_number || "_none"}
                                  onValueChange={(v) => pickItemForMat(kind, idx, v === "_none" ? "" : v)}>
                            <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`pm-${kind}-item-${idx}`}>
                              <SelectValue placeholder="Elegir del maestro…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">—</SelectItem>
                              {items.slice(0, 200).map((it) => (
                                <SelectItem key={it.id} value={it.item_number}>
                                  {it.item_number} — {it.description?.slice(0, 40)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          <Input value={m.description}
                                 onChange={(e) => updateMat(kind, idx, { description: e.target.value })}
                                 className="rounded-none h-8 text-xs"
                                 data-testid={`pm-${kind}-desc-${idx}`} />
                        </td>
                        <td className="p-1">
                          <Input type="number" min="0" step="any" value={m.qty}
                                 onChange={(e) => updateMat(kind, idx, { qty: Number(e.target.value) })}
                                 className="rounded-none h-8 text-xs text-right tabular-nums"
                                 data-testid={`pm-${kind}-qty-${idx}`} />
                        </td>
                        <td className="p-1">
                          <Input value={m.uom} onChange={(e) => updateMat(kind, idx, { uom: e.target.value })}
                                 className="rounded-none h-8 text-xs"
                                 data-testid={`pm-${kind}-uom-${idx}`} />
                        </td>
                        <td className="p-1 text-right">
                          <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-destructive"
                                  onClick={() => removeMat(kind, idx)}
                                  data-testid={`pm-${kind}-remove-${idx}`}>
                            <Trash2 size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}

          <div className="border-t mt-3 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Gama de tareas (checklist) ({form.tasks.length})
              </div>
              <Button size="sm" variant="outline" className="rounded-none text-[10px] font-bold h-7"
                      onClick={addTask} data-testid="pm-add-task">
                <Plus size={11} className="mr-1" /> Agregar tarea
              </Button>
            </div>
            {form.tasks.length > 0 && (
              <table className="w-full text-xs border">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-1 text-left w-14">#</th>
                    <th className="p-1 text-left w-40">Categoría</th>
                    <th className="p-1 text-left">Tarea</th>
                    <th className="p-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.tasks.map((tk, idx) => (
                    <tr key={idx} className="border-t" data-testid={`pm-task-row-${idx}`}>
                      <td className="p-1">
                        <Input type="number" min="0" value={tk.order}
                               onChange={(e) => updateTask(idx, { order: Number(e.target.value) })}
                               className="rounded-none h-8 text-xs text-center tabular-nums" />
                      </td>
                      <td className="p-1">
                        <Input value={tk.category} placeholder="Motor, Chasis…"
                               onChange={(e) => updateTask(idx, { category: e.target.value })}
                               className="rounded-none h-8 text-xs"
                               data-testid={`pm-task-cat-${idx}`} />
                      </td>
                      <td className="p-1">
                        <Input value={tk.description}
                               onChange={(e) => updateTask(idx, { description: e.target.value })}
                               className="rounded-none h-8 text-xs"
                               data-testid={`pm-task-desc-${idx}`} />
                      </td>
                      <td className="p-1 text-right">
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-destructive"
                                onClick={() => removeTask(idx)}
                                data-testid={`pm-task-remove-${idx}`}>
                          <Trash2 size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4 border-t pt-3">
            <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                    onClick={() => setOpen(false)} data-testid="pm-cancel">
              Cancelar
            </Button>
            <Button size="sm" className="rounded-none text-xs font-bold" onClick={save} data-testid="pm-save">
              <Save size={12} className="mr-1" /> Guardar plantilla
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
